import { navigate } from '@reach/router';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components/macro';
import { useApiRequest } from '../../api/useApiRequest';
import { useInterval } from '../../helpers/hooks/useInterval';
import { RouteComponentProps } from '../../helpers/RouteComponentProps';
import { ReactComponent as SearchIcon } from '../../images/icons/search.svg';
import { PageContainer } from '../../layout/Layout';
import { Alert } from '../../shared/Alert';
import { EmptyPlaceholder } from '../../shared/EmptyPlaceholder';
import { Header1 } from '../../shared/Headers';
import { PageLoadingIndicator } from '../../shared/LoadingIndicator';
import { PaginationControl } from '../../shared/PaginationControl';
import { ClickableTr, Table, TBody, Td, Th, THead, Tr } from '../../shared/Table';
import { TextInputWithIcon } from '../../shared/TextInputWithIcon';
import { fadeIn, flash } from '../../styling/animations';
import { colourNeutral04 } from '../../styling/colours';
import { fontSize01, fontSize03, fontWeightBold } from '../../styling/fonts';
import { spacing01, spacing04 } from '../../styling/spacing';
import { projectUrls } from './projectUrls';

export const noProjectsMessageTestId = 'no-projects-message';
export const errorTestId = 'error';

export type ProjectSummary = {
  projectId: number;
  siteAddress: string;
  projectReference: string;
  customerReference: string;
  customerName: string;
};

export type ProjectsResponse = {
  projects: Array<ProjectSummary>;
  totalNumberOfPages: number;
  lastSynchronisedFromNetSuite: string | null;
};

type State = {
  currentPageNumber: number;
  searchTerm: string;
  queriedSearchTerm: string;
  latestResponse: ProjectsResponse | null;
  isPerformingSilentRefresh: boolean;
};

const initialState: State = {
  currentPageNumber: 1,
  searchTerm: '',
  queriedSearchTerm: '',
  latestResponse: null,
  isPerformingSilentRefresh: false,
};

type ProjectsComponent = {
  (props: RouteComponentProps): JSX.Element;
};

export const Projects: ProjectsComponent = () => {
  const { response, inProgress, error, sendRequest } = useApiRequest<ProjectsResponse>({
    httpMethod: 'get',
    endpointUrl: 'projects',
  });

  const [state, setState] = useState<State>(initialState);
  const { currentPageNumber, searchTerm, queriedSearchTerm, latestResponse } = state;

  const activeQuery = useRef<{ pageNumber: string; searchTerm: string }>();

  useEffect(() => {
    setState(previousState => ({ ...previousState, isPerformingSilentRefresh: false }));
    const query = { pageNumber: currentPageNumber.toString(), searchTerm: queriedSearchTerm.trim() };
    void sendRequest({ queryStringParameters: query });
    activeQuery.current = query;
  }, [currentPageNumber, queriedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (response != null) {
      setState(currentState => ({ ...currentState, latestResponse: response }));
    }
  }, [response]);

  const initialResponse = useRef<ProjectsResponse | null>(null);
  useEffect(() => {
    if (initialResponse.current == null && latestResponse != null) {
      initialResponse.current = latestResponse;
    }
  }, [initialResponse, latestResponse]);

  const debouncedSetQueriedSearchTerm = useRef(
    debounce((newSearchTerm: string) => {
      setState(currentState => ({ ...currentState, queriedSearchTerm: newSearchTerm, currentPageNumber: 1 }));
    }, 500 /* milliseconds */),
  );

  useEffect(() => {
    debouncedSetQueriedSearchTerm.current(searchTerm);
  }, [searchTerm]);

  useEffect(() => debouncedSetQueriedSearchTerm.current.cancel, []);

  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setState(currentState => ({ ...currentState, searchTerm: event.target.value }));
  };

  const onChangePageNumber = (newPageNumber: number) =>
    setState(currentState => ({ ...currentState, currentPageNumber: newPageNumber }));

  const inProgressRef = useRef<boolean>(inProgress);
  useEffect(() => {
    inProgressRef.current = inProgress;
  }, [inProgress]);

  const performSilentRefresh = () => {
    if (!inProgressRef.current) {
      setState(previousState => ({ ...previousState, isPerformingSilentRefresh: true }));
      void sendRequest({ queryStringParameters: activeQuery.current });
    }
  };

  useInterval(() => performSilentRefresh(), 30 * 1000);

  useEffect(() => {
    if (state.isPerformingSilentRefresh && !inProgress) {
      setState(previousState => ({ ...previousState, isPerformingSilentRefresh: false }));
    }
  }, [inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageContainer>
      <HeaderBar>
        <Header1>Projects</Header1>
        {latestResponse && latestResponse.lastSynchronisedFromNetSuite && (
          <LastSyncedContainer>
            <LastSyncedLabel>Last Synced</LastSyncedLabel>
            <LastSyncedTime
              $shouldFlash={
                initialResponse.current != null &&
                latestResponse.lastSynchronisedFromNetSuite !== initialResponse.current.lastSynchronisedFromNetSuite
              }
              key={latestResponse.lastSynchronisedFromNetSuite}
            >
              {latestResponse.lastSynchronisedFromNetSuite}
            </LastSyncedTime>
          </LastSyncedContainer>
        )}
      </HeaderBar>
      <SearchInput
        value={searchTerm}
        placeholder="Search by PR Number, Address, Customer NS or Name..."
        onChange={onChangeSearchInput}
        icon={<SearchIcon />}
        maxLength={100}
      />
      {error != null && (
        <Alert
          type="error"
          title="Something went wrong"
          body={
            error.userVisibleMessage ||
            `An unexpected error occurred while attempting to load the Projects. Please try again.`
          }
          data-testid={errorTestId}
        />
      )}
      {error == null && (
        <>
          {latestResponse != null ? (
            <>
              <TableContainer>
                <ProjectsTable>
                  <THead>
                    <Tr>
                      <ProjectNumberHeader>PR Number</ProjectNumberHeader>
                      <CustomerHeader>Customer</CustomerHeader>
                      <SiteAddressHeader>Site Address</SiteAddressHeader>
                    </Tr>
                  </THead>
                  <TBody>
                    {latestResponse.projects.map(project => (
                      <ClickableTr key={project.projectId} onClick={navigateToProject(project.projectId)}>
                        <Td>{project.projectReference}</Td>
                        <Td>
                          <CustomerText>{project.customerName}</CustomerText>
                          <CustomerText>{project.customerReference}</CustomerText>
                        </Td>
                        <Td>{project.siteAddress}</Td>
                      </ClickableTr>
                    ))}
                    {latestResponse.projects.length === 0 && (
                      <Tr>
                        <Td colSpan={3} data-testid={noProjectsMessageTestId}>
                          <EmptyPlaceholder message="No matching Projects were found." />
                        </Td>
                      </Tr>
                    )}
                  </TBody>
                </ProjectsTable>
                {(inProgress || response !== latestResponse) && !state.isPerformingSilentRefresh && (
                  <TableLoadingOverlay>
                    <PageLoadingIndicator />
                  </TableLoadingOverlay>
                )}
              </TableContainer>
              {latestResponse.totalNumberOfPages > 0 && (
                <PaginationControl
                  currentPageNumber={currentPageNumber}
                  onChangeCurrentPageNumber={onChangePageNumber}
                  totalNumberOfPages={latestResponse.totalNumberOfPages}
                />
              )}
            </>
          ) : (
            <>{inProgress && <PageLoadingIndicator />}</>
          )}
        </>
      )}
    </PageContainer>
  );
};

const navigateToProject = (projectId: number) => (event: React.MouseEvent) => {
  if (event.ctrlKey || event.metaKey) {
    window.open(projectUrls.projectDetails(projectId));
  } else {
    void navigate(projectUrls.projectDetails(projectId));
  }
  event.stopPropagation();
  return false;
};

const SearchInput = styled(TextInputWithIcon)`
  margin-bottom: ${spacing04};
`;

const TableContainer = styled.div`
  position: relative;
`;

const ProjectsTable = styled(Table)`
  margin-bottom: ${spacing04};
  table-layout: fixed;
`;

const ProjectNumberHeader = styled(Th)`
  width: 20%;
`;

const CustomerHeader = styled(Th)`
  width: 30%;
`;

const SiteAddressHeader = styled(Th)`
  width: 50%;
`;

const pulse = keyframes`
  from {
    background-color: rgba(255, 255, 255, 0.65);
  }
  
  to {
    background-color: rgba(255, 255, 255, 0.85);
  }
`;

const TableLoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background-color: rgba(255, 255, 255, 0.7);
  animation: ${fadeIn} 0.25s ease forwards, ${pulse} 0.75s infinite alternate both;
`;

const CustomerText = styled.p`
  margin: 0;
  line-height: 1.8;
`;

const HeaderBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const LastSyncedContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  margin-bottom: ${spacing04};
`;

const LastSyncedLabel = styled.label`
  text-transform: uppercase;
  margin-bottom: ${spacing01};
  font-weight: ${fontWeightBold};
  font-size: ${fontSize01};
  letter-spacing: 0.5px;
  color: ${colourNeutral04};
`;

const LastSyncedTime = styled.div<{ $shouldFlash: boolean }>`
  ${props =>
    props.$shouldFlash &&
    css`
      animation: ${flash} 3s ease both;
    `};
  font-size: ${fontSize03};
  letter-spacing: 1px;
  font-weight: ${fontWeightBold};
`;
